import { trackEvent } from "@/lib/analytics";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { issueMcpKey } from "@/lib/aiAccess.functions";

/** Monthly AI Access price, shown in the UI. Charged server-side. */
export const AI_ACCESS_PRICE_LABEL = "$25";
export const AI_ACCESS_PRICE_PERIOD = "per month";
/** Credits granted with each paid AI Access month (mirrors the edge default). */
export const AI_ACCESS_MONTHLY_CREDITS = 300;

export type AiAccessStatus =
  | "none"
  | "pending"
  | "active"
  | "past_due"
  | "cancelled"
  | "expired";

export interface AiAccessKey {
  id: string;
  label: string | null;
  active: boolean;
  rate_limit_per_min: number | null;
  created_at: string;
  last_used_at: string | null;
}

export interface AiAccessState {
  active: boolean;
  status: AiAccessStatus;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  keys: AiAccessKey[];
}

const IDLE: AiAccessState = {
  active: false,
  status: "none",
  current_period_end: null,
  cancel_at_period_end: false,
  keys: [],
};

/** Refresh a near-expiry session so checkout never fails with an opaque 401. */
async function ensureValidSession() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Please sign in to manage Statlign AI Access.");
  const expiresAt = (session.expires_at ?? 0) * 1000;
  if (expiresAt - Date.now() < 60_000) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error("Your session has expired. Please sign in again.");
    }
  }
}

/**
 * Authoritative AI Access state for the signed-in member.
 *
 * The answer comes from the database (`my_ai_access_status`), which reads the
 * subscription written only by the Paystack webhook — the client cannot grant
 * itself access by editing local state. Key metadata never includes the hash.
 */
export function useAiAccess() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["ai-access-status", user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async (): Promise<AiAccessState> => {
      const { data, error } = await supabase.rpc("my_ai_access_status");
      if (error) throw error;
      return { ...IDLE, ...((data ?? {}) as Partial<AiAccessState>) };
    },
  });

  const state = user ? (query.data ?? IDLE) : IDLE;

  return {
    ...state,
    isLoading: !!user && query.isLoading,
    isSignedIn: !!user,
    refetch: query.refetch,
  };
}

/** Starts Paystack checkout for the $25/month AI Access plan. */
export function useStartAiAccessCheckout() {
  return useMutation({
    mutationFn: async () => {
      await ensureValidSession();
      trackEvent("begin_checkout", { product: "ai_access" });
      const { data, error } = await supabase.functions.invoke("paystack-subscribe", {
        body: { origin: window.location.origin, tier: "ai_access" },
      });
      if (error) throw error;
      if (data?.already_active) return data as { already_active: true };
      if (!data?.checkout_url) throw new Error("Could not start checkout. Please try again.");
      window.location.href = data.checkout_url as string;
      return data as { checkout_url: string };
    },
  });
}

/** Confirms a checkout after Paystack redirects back to /ai-access. */
export function useVerifyAiAccessCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reference: string) => {
      await ensureValidSession();
      const { data, error } = await supabase.functions.invoke("paystack-premium-verify", {
        body: { reference },
      });
      if (error) throw error;
      return data as { status: string; tier?: string; current_period_end?: string };
    },
    onError: () => trackEvent("payment_failed", { product: "ai_access", status: "verify_error" }),
    onSuccess: (res) => {
      if (res?.status === "active") trackEvent("subscribe", { product: "ai_access", value: 25, currency: "USD" });
      else if (res?.status && res.status !== "pending") trackEvent("payment_failed", { product: "ai_access", status: res.status });
      void queryClient.invalidateQueries({ queryKey: ["ai-access-status"] });
      void queryClient.invalidateQueries({ queryKey: ["credit-wallet"] });
    },
  });
}

/** Cancels renewal (access runs to period end) or reactivates the AI plan. */
export function useManageAiAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (action: "cancel" | "reactivate") => {
      await ensureValidSession();
      const { data, error } = await supabase.functions.invoke("paystack-premium-manage", {
        body: { action, tier: "ai_access" },
      });
      if (error) throw error;
      if (data && data.ok === false) throw new Error(data.error ?? "Please try again in a moment.");
      return data as { ok: boolean; action: string };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ai-access-status"] });
    },
  });
}

/**
 * Issues or rotates the member's MCP key.
 *
 * The plaintext key is returned exactly once by the server; only its hash is
 * stored, so it can never be read back — only replaced.
 */
export function useIssueMcpKey() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await ensureValidSession();
      return await issueMcpKey();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["ai-access-status"] });
    },
  });
}
